import { useEffect, useRef, useState } from 'react'
import type { SetStateAction } from 'react'
import {
  prepareSkrottlappAugmentation,
  SampleFormContext,
  SampleFormData,
  Mtds,
  Geometry
} from 'prove-common'
import { extractCoordinates } from '../utils/sampleUtils'
import { fields, widgets, templates } from '../components/ui-schema-components'
import { customizeValidator } from '@rjsf/validator-ajv8'
import localize_nb from 'ajv-i18n/localize/nb'
import { FormValidation, RJSFSchema, RJSFValidationError } from '@rjsf/utils'
import { IChangeEvent, ThemeProps, withTheme } from '@rjsf/core'
import { useAuth } from './useAuth'
import { toUserMessage } from '../utils/userErrorUtils'
import { useQueryClient } from '@tanstack/react-query'
import { getLdfProducerGeoOptions } from './useLdfProducerGeo'
import { type Activity } from '../types/types'
import {
  requiredFieldMessage,
  requiredFieldPhrase
} from '../utils/requiredFieldMessage'
import definitions from '../../../../../schemas/definitions.json'
import conditionals from '../../../../../schemas/conditionals.json'

type UseSampleFormParams = {
  schema?: RJSFSchema
  existingFormData?: Partial<SampleFormData>
  isPartiallyEditable?: boolean
  position?:
    GeolocationPosition | { longitude: number; latitude: number } | Geometry
}

const conditionalRequiredFieldTitles: Record<string, string> =
  Object.fromEntries(
    Object.values(conditionals).flatMap((conditional) => {
      const block = conditional as {
        then?: { properties?: Record<string, unknown> }
        else?: { properties?: Record<string, unknown> }
      }
      const props = [
        ...Object.keys(block.then?.properties ?? {}),
        ...Object.keys(block.else?.properties ?? {})
      ]

      return props.flatMap((key) => {
        const definition = definitions[key as keyof typeof definitions]
        return definition && 'title' in definition
          ? [[key, definition.title]]
          : []
      })
    })
  )

const FOREBEHANDLING_TITLES: Record<string, string> = {
  nivaa2: 'Forbehandling',
  nivaa3: 'Spesifisering av forbehandling'
}

const readableRequiredTitle = (propertyKey: string): string | undefined => {
  const conditional = conditionalRequiredFieldTitles[propertyKey]
  if (conditional) return conditional
  return FOREBEHANDLING_TITLES[propertyKey]
}

const prettifyInternalKey = (propertyKey: string): string => {
  const spaced = propertyKey.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

type SchemaTitleSource = {
  title?: unknown
  $ref?: unknown
  properties?: Record<string, SchemaTitleSource>
}

const definitionTitleOf = (
  source: SchemaTitleSource | undefined,
  definitions: Record<string, SchemaTitleSource> | undefined
): string | undefined => {
  const ref = source?.$ref
  if (typeof ref !== 'string' || !ref.startsWith('#/definitions/')) {
    return undefined
  }
  const title = definitions?.[ref.slice('#/definitions/'.length)]?.title
  return typeof title === 'string' && title.trim() !== '' ? title : undefined
}

/**
 * Norwegian title for a sample-form property from the canonical
 * definitions.json, searching both top-level definitions and nested
 * properties (e.g. `produsent.tilsynsobjektId` -> "Tilsynsobjekt-ID").
 *
 * When the containing path is known (RJSF's `property` for a required error
 * points at the parent object), the nested lookup is scoped to that parent so
 * that a key reused across containers resolves to its own title — e.g.
 * `externalReferenceId` is "Lokalitetsnummer" under `lokalitet` and
 * "Produsentnummer" under `produsent`.
 */
const canonicalTitleFor = (
  propertyKey: string,
  propertyPath?: string
): string | undefined => {
  const definitionsOf = definitions as Record<
    string,
    SchemaTitleSource | undefined
  >
  const isUsable = (value: unknown): value is string =>
    typeof value === 'string' && value.trim() !== ''

  // Path-scoped lookup: the deepest container in the path that owns
  // `propertyKey` wins, because sample objects nest in containers (payload,
  // produsent, lokalitet, ...) that each define their own properties.
  if (propertyPath) {
    const segments = propertyPath
      .replace(/\[\d+\]/g, '')
      .split('.')
      .filter(Boolean)
    for (let i = segments.length - 1; i >= 0; i--) {
      const nested =
        definitionsOf[segments[i]]?.properties?.[propertyKey]?.title
      if (isUsable(nested)) return nested
    }
  }

  const topLevel = definitionsOf[propertyKey]
  if (isUsable(topLevel?.title)) return topLevel.title

  for (const definition of Object.values(definitionsOf)) {
    const nested = definition?.properties?.[propertyKey]?.title
    if (isUsable(nested)) return nested
  }
  return undefined
}

/**
 * Norwegian label for a missing required sample-form property, resolved from
 * the shared definitions.json (Norwegian titles for every known sample field,
 * including top-level fields such as nameRedacted and nested ones like
 * produsent.tilsynsobjektId) and the backend-provided schema (payload data
 * fields like "Tilstand ved prøveuttak") instead of prettified internal keys
 * ("Name Redacted"). Returns undefined when nothing can answer so
 * `prettifyInternalKey` remains the last-resort fallback.
 */
const buildRequiredTitleResolver =
  (schema?: RJSFSchema) =>
  (propertyKey: string, propertyPath?: string): string | undefined => {
    // Canonical Norwegian titles for all known sample fields live in the same
    // definitions.json that drives the conditional titles above. Pass the
    // containing path so reused keys resolve to the right container's title.
    const canonical = canonicalTitleFor(propertyKey, propertyPath)
    if (canonical) return canonical

    const properties = schema?.properties as
      Record<string, SchemaTitleSource> | undefined
    const payloadProperties = properties?.payload?.properties as
      Record<string, SchemaTitleSource> | undefined
    const schemaDefinitions = schema?.definitions as
      Record<string, SchemaTitleSource> | undefined

    for (const source of [
      properties?.[propertyKey],
      payloadProperties?.[propertyKey]
    ]) {
      const title = source?.title
      if (typeof title === 'string' && title.trim() !== '') return title
      const definitionTitle = definitionTitleOf(source, schemaDefinitions)
      if (definitionTitle) return definitionTitle
    }

    return undefined
  }

export const transformSampleFormErrors = (
  errors: RJSFValidationError[],
  resolveRequiredTitle?: (
    propertyKey: string,
    propertyPath?: string
  ) => string | undefined
) =>
  errors
    // if/then/else conditionals (e.g. produksjonstype -> verdikjede) always
    // report their failing branch's own errors, such as a missing required
    // follower field; the AJV "if" wrapper ("must match ... schema") only
    // adds raw English noise, so drop it for every conditional.
    .filter((e) => e.name !== 'if')
    .map((e) => {
      if (e.name === 'uniqueItems' && e.property === '.payload.subSamples') {
        e.message = 'Delprøvene må være unike'
      }
      if (e.name === 'required') {
        const missingProperty = e.params?.missingProperty
        if (typeof missingProperty === 'string') {
          const label =
            missingProperty === 'payload'
              ? 'informasjon om prøven'
              : (readableRequiredTitle(missingProperty) ??
                resolveRequiredTitle?.(missingProperty, e.property) ??
                prettifyInternalKey(missingProperty))
          const userLabel = requiredFieldPhrase(label)
          e.message = requiredFieldMessage(label)
          e.stack = `${e.property} ${userLabel} mangler`
        }
      }
      if (e.name === 'type' || e.name === 'format') {
        e.message = 'Feltet må ha riktig format'
      }
      if (e.name === 'enum') {
        e.message = 'Du må velge en av de oppgitte verdiene'
      }
      return e
    })

const theme: ThemeProps = { widgets, fields, templates }
export const ThemedForm = withTheme<
  SampleFormData,
  RJSFSchema,
  SampleFormContext
>(theme)

export function useSampleForm({
  schema,
  existingFormData,
  isPartiallyEditable,
  position
}: UseSampleFormParams) {
  const auth = useAuth()
  const queryClient = useQueryClient()
  const [isFetchingSkrottlappData, setIsFetchingSkrottlappData] =
    useState(false)

  const coords = extractCoordinates(position)

  const initialFormData: SampleFormData = {
    takenDate: new Date().toISOString().split('T')[0],
    ...existingFormData,
    payload: {
      ...existingFormData?.payload
    }
  } as SampleFormData

  const [formData, setFormDataState] = useState<SampleFormData>(initialFormData)
  const latestFormDataRef = useRef(formData)

  const setFormData = (action: SetStateAction<SampleFormData>) => {
    const nextFormData =
      typeof action === 'function' ? action(latestFormDataRef.current) : action
    latestFormDataRef.current = nextFormData
    setFormDataState(nextFormData)
  }

  const hasKommunenrInSchema =
    schema != null &&
    Boolean(schema.properties && 'kommunenr' in schema.properties)

  const currentProdnr = formData.produsent?.externalReferenceId
  const lastResolvedProdnrRef = useRef<string | undefined>(
    existingFormData?.produsent?.externalReferenceId
  )

  useEffect(() => {
    if (!hasKommunenrInSchema) return
    if (!currentProdnr) {
      lastResolvedProdnrRef.current = undefined
      return
    }

    if (
      currentProdnr === lastResolvedProdnrRef.current &&
      formData.payload?.kommunenr != null
    ) {
      return
    }

    lastResolvedProdnrRef.current = currentProdnr

    let cancelled = false
    const resolveKommunenr = async () => {
      try {
        const geoList = await queryClient.fetchQuery(
          getLdfProducerGeoOptions(auth, currentProdnr)
        )
        if (cancelled) return
        const matched = geoList?.find(
          (item) =>
            item?.kommunenr != null && String(item.kommunenr).trim() !== ''
        )
        if (matched?.kommunenr) {
          const formatted = String(matched.kommunenr).trim().padStart(4, '0')
          setFormData((prev) => ({
            ...prev,
            payload: {
              ...prev.payload,
              kommunenr: formatted
            }
          }))
        }
      } catch {
        // Ignore error - allow manual input
      }
    }

    resolveKommunenr()

    return () => {
      cancelled = true
    }
  }, [
    currentProdnr,
    hasKommunenrInSchema,
    formData.payload?.kommunenr,
    auth,
    queryClient
  ])

  const validator = customizeValidator<
    SampleFormData,
    RJSFSchema,
    SampleFormContext
  >({}, localize_nb)

  const customValidate = (
    data: SampleFormData | undefined,
    errors: FormValidation<SampleFormData>
  ) => {
    const subSamples = data?.payload?.subSamples
    if (subSamples) {
      const uniqueIds = new Set(subSamples.map((s) => s.subSampleId))
      if (uniqueIds.size !== subSamples.length) {
        errors?.payload?.subSamples?.addError('Delprøvene må ha unike numre')
      }
    }

    const sisteEktoparasittbehandling =
      data?.payload?.sisteEktoparasittbehandling
    if (
      sisteEktoparasittbehandling &&
      typeof sisteEktoparasittbehandling === 'string'
    ) {
      const today = new Date().toISOString().split('T')[0]
      if (sisteEktoparasittbehandling > today) {
        errors?.payload?.sisteEktoparasittbehandling?.addError(
          'Dato kan ikke være i fremtiden'
        )
      }
    }

    return errors
  }

  const scanSkrottlapp = async (
    code: string,
    fieldId: string,
    activity: Activity
  ): Promise<void> => {
    setIsFetchingSkrottlappData(true)
    try {
      const prepared = await prepareSkrottlappAugmentation({
        activity,
        code,
        auth,
        queryClient
      })
      const applied = prepared.applyWithWarnings(
        fieldId,
        latestFormDataRef.current
      )
      setFormData(applied.formData)
      if (prepared.additionalDataError) {
        Mtds.toast.danger(
          toUserMessage(prepared.additionalDataError, 'hente skrottlappdata')
        )
      }
      if (applied.warning) Mtds.toast.danger(applied.warning)
    } catch (err) {
      Mtds.toast.danger(toUserMessage(err, 'hente skrottlappdata'))
    } finally {
      setIsFetchingSkrottlappData(false)
    }
  }

  const onScanSkrottlapp = (
    code: string,
    fieldId: string,
    activity: Activity
  ) => {
    if (!fieldId || !code) return

    scanSkrottlapp(code, fieldId, activity)
  }

  const formContext: SampleFormContext = {
    isFetching: isFetchingSkrottlappData,
    onScanSkrottlapp,
    position: coords
  }

  const onChange = (data: IChangeEvent<SampleFormData>) => {
    if (data.formData) {
      setFormData((prev) => {
        let next = data.formData as SampleFormData

        const previousForbehandling = prev.payload?.forbehandling
        const nextForbehandling = next.payload?.forbehandling
        if (
          previousForbehandling?.nivaa2 !== nextForbehandling?.nivaa2 &&
          nextForbehandling?.nivaa3 !== undefined
        ) {
          const forbehandlingWithoutSpecification = { ...nextForbehandling }
          delete forbehandlingWithoutSpecification.nivaa3
          next = {
            ...next,
            payload: {
              ...next.payload,
              forbehandling: forbehandlingWithoutSpecification
            }
          }
        }

        if (isPartiallyEditable) return next

        const producerOrLocalityChanged =
          prev.produsent?.tilsynsobjektId !== next.produsent?.tilsynsobjektId ||
          prev.produsent?.externalReferenceId !==
            next.produsent?.externalReferenceId ||
          prev.lokalitet?.externalReferenceId !==
            next.lokalitet?.externalReferenceId

        if (!producerOrLocalityChanged) {
          return next
        }

        const producerChanged =
          prev.produsent?.externalReferenceId !==
          next.produsent?.externalReferenceId

        let updatedPayload = next.payload
        if (
          producerChanged &&
          hasKommunenrInSchema &&
          prev.produsent?.externalReferenceId != null
        ) {
          updatedPayload = {
            ...updatedPayload,
            kommunenr: undefined
          }
        }

        const subSamples = next.payload?.subSamples
        if (subSamples == null || subSamples.length === 0) {
          return {
            ...next,
            payload: updatedPayload
          }
        }

        return {
          ...next,
          payload: {
            ...updatedPayload,
            subSamples: subSamples.map((subSample) => ({
              subSampleId: subSample.subSampleId,
              payload: {}
            }))
          }
        }
      })
    } else {
      Mtds.toast.danger('Det har oppstått en feil ved lagring av skjemaet')
    }
  }

  const reset = () => setFormData(initialFormData)

  return {
    initialFormData,
    formData,
    setFormData,
    validator,
    customValidate,
    transformErrors: (errors: RJSFValidationError[]) =>
      transformSampleFormErrors(errors, buildRequiredTitleResolver(schema)),
    formContext,
    isFetching: isFetchingSkrottlappData,
    onChange,
    reset
  }
}
